import React, { useCallback, useEffect, useState } from 'react';
import { Bell, Check, Trash2, Mail, MessageSquare, Star, AlertCircle, FileText, Loader, BellPlus, BellDot, BellMinus } from 'lucide-react';
import './style.scss';
import { useCookies } from 'react-cookie';
import { getData, patchData, postData, putData } from '../../../../services';
import { useCommunityNotificationContext } from '../../../../Context/notificationContext';

const Notifications = () => {
  const [filter, setFilter] = useState('all');
  const [cookies] = useCookies([]);
  const [notificationData, setNotificationData] = useState([]);
  const [loading, setLoading] = useState(false);

  const { setMarkAllAsRead } = useCommunityNotificationContext();

  // Get all notifications
  const getAllNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getData({
        endpoint: `Notification/employee/${cookies.euid}`,
        params: { employeeId: cookies.euid },
        token: cookies.e_t,
      });
      if (response) {

        setNotificationData(response?.data || []);
      }
    } catch (error) {
      console.error("Error fetching all notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [cookies.euid, cookies.e_t]);

  // Get unread notifications
  const getAllUnreadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getData({
        endpoint: `Notification/employee/${cookies.euid}/unread`,
        params: { employeeId: cookies.euid },
        token: cookies.e_t,
      });
      if (response) {

        setNotificationData(response?.data || []);
      }
    } catch (error) {
      console.error("Error fetching unread notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [cookies.euid, cookies.e_t]);

  // Get read notifications
  const getAllReadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getData({
        endpoint: `Notification/employee/${cookies.euid}/read`,
        params: { employeeId: cookies.euid },
        token: cookies.e_t,
      });
      if (response) {

        setNotificationData(response?.data || []);
      }
    } catch (error) {
      console.error("Error fetching read notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [cookies.euid, cookies.e_t]);

  // Handle mark single notification as read
  const markRead = async (notificationId) => {
    try {
      const response = await patchData({
        endpoint: `Notification/${notificationId}/read`,
        params: { notificationId: notificationId },
        token: cookies.e_t,
      });
      if (response && response.status) {
        // Update local state to reflect read status
        setNotificationData(prev =>
          prev.map(notif =>
            notif.id === notificationId ? { ...notif, isRead: true } : notif
          )
        );

        const allRead = notificationData.length > 0 &&
          notificationData.every(notif => Boolean(notif.isRead) === true);

        if (allRead) {
          setMarkAllAsRead(true);
        }
        getAllNotifications();

      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  // Handle mark all notifications as read
  const markAllAsRead = async () => {
    try {
      const response = await patchData({
        endpoint: `Notification/employee/${cookies.euid}/read-all`,
        params: { employeeId : cookies.euid ,},
        token: cookies.e_t,
      });
      if (response && response.status) {
        // Update local state to reflect all read
        setNotificationData(prev => 
          prev.map(notif => ({ ...notif, isRead: true }))
        );
        getAllNotifications();
        setMarkAllAsRead(true);
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  // Handle deleting a notification
  const deleteNotification = async (id) => {
    try {
      const response = await postData({
        endpoint: 'Notification/delete',
        data: { notificationId: id },
        token: cookies.e_t,
      });
      if (response && response.success) {
        setNotificationData(prev => prev.filter(notif => notif.id !== id));
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  // Get icon based on notification type
  const getIcon = (type) => {
    switch(type) {
      case 'TaskDeleted':
        return <BellMinus color="#8F95B2"  size={18} />;
      case 'TaskUpdated':
        return <BellDot color="#8F95B2" size={18} />;
      case 'TaskAssigned':
        return <BellPlus color="#8F95B2" size={18} />;
      default:
        return <Bell color="#8F95B2"  size={18} />;
    }
  };

  // Load appropriate data when filter changes

  useEffect(() => {
    if (notificationData.length > 0) {
      const allRead = notificationData.every(notif => notif.isRead);
      setMarkAllAsRead(allRead);
    } else {
      setMarkAllAsRead(true); // No notifications means "all read" by default
    }
  }, [notificationData, setMarkAllAsRead]);


  useEffect(() => {
    if (filter === 'all') {
      getAllNotifications();
    } else if (filter === 'unread') {
      getAllUnreadNotifications();
    } else if (filter === 'read') {
      getAllReadNotifications();
    }
  }, [filter, getAllNotifications, getAllUnreadNotifications, getAllReadNotifications]);

  return (
    <div className="notifications-container">
      <div className="notifications-filters">
        <div className="filter-buttons">
          <button 
            onClick={() => setFilter('all')}
            className={filter === 'all' ? 'active' : ''}
          >
            All
          </button>
          <button 
            onClick={() => setFilter('unread')}
            className={filter === 'unread' ? 'active' : ''}
          >
            Unread
          </button>
          <button 
            onClick={() => setFilter('read')}
            className={filter === 'read' ? 'active' : ''}
          >
            Read
          </button>
        </div>
        {filter !== 'read' ? <div className="header-actions">
          <button onClick={markAllAsRead} className="mark-all-read">
            <Check size={16} />
            Mark all as read
          </button>
        </div> : null }
        
      </div>

      <div className="notifications-list">
        {loading ? (
          <div className="loading-state">
             <Loader className="animate-spin" size={24} />
          </div>
        ) : notificationData && notificationData.length > 0 ? (
          notificationData.map((notification) => (
            <div 
              key={notification.id} 
              className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
            >
              <div className="notification-icon">
                {getIcon(notification.type)}
              </div>
              <div className="notification-content">
                <p className="notification-text">{notification.message}</p>
                <p className="notification-time">{new Date(notification.createdDate).toLocaleString()}</p>
              </div>
              <div className="notification-actions">
                {!notification.isRead && (
                  <button 
                    onClick={() => markRead(notification.id)}
                    className="action-button read"
                    title="Mark as read"
                  >
                    <Check size={16} />
                  </button>
                )}
                {/* <button 
                  onClick={() => deleteNotification(notification.id)}
                  className="action-button delete"
                  title="Delete notification"
                >
                  <Trash2 size={16} />
                </button> */}
              </div>
            </div>
          ))
        ) : (
          <div className="empty-state">
            <div className="empty-icon">
              <Bell size={24} />
            </div>
            <p className="empty-title">No notifications found</p>
            <p className="empty-subtitle">
              {filter !== 'all' ? 'Try changing your filter selection' : 'You\'re all caught up!'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;


// import React, { useCallback, useEffect, useState } from 'react';
// import { Bell, Check, Trash2, Mail, MessageSquare, Star, AlertCircle, FileText } from 'lucide-react';
// import './style.scss';
// import { useCookies } from 'react-cookie';
// import { getData, patchData, postData, putData } from '../../../../services';

// const Notifications = () => {
//   const [filter, setFilter] = useState('all');
//   const [cookies] = useCookies([]);
//   const [notificationData, setNotificationData] = useState([]);
//   const [loading, setLoading] = useState(false);

//   // Get all notifications
//   const getAllNotifications = useCallback(async () => {
//     setLoading(true);
//     try {
//       const response = await getData({
//         endpoint: `Notification/employee/${cookies.euid}`,
//         params: { employeeId: cookies.euid },
//         token: cookies.e_t,
//       });
//       if (response) {
//         setNotificationData(response?.data || []);
//       }
//     } catch (error) {
//       console.error("Error fetching all notifications:", error);
//     } finally {
//       setLoading(false);
//     }
//   }, [cookies.euid, cookies.e_t]);

//   // Get unread notifications
//   const getAllUnreadNotifications = useCallback(async () => {
//     setLoading(true);
//     try {
//       const response = await getData({
//         endpoint: `Notification/employee/${cookies.euid}/unread`,
//         params: { employeeId: cookies.euid },
//         token: cookies.e_t,
//       });
//       if (response) {
//         setNotificationData(response?.data || []);
//       }
//     } catch (error) {
//       console.error("Error fetching unread notifications:", error);
//     } finally {
//       setLoading(false);
//     }
//   }, [cookies.euid, cookies.e_t]);

//   // Get read notifications
//   const getAllReadNotifications = useCallback(async () => {
//     setLoading(true);
//     try {
//       const response = await getData({
//         endpoint: `Notification/employee/${cookies.euid}/read`,
//         params: { employeeId: cookies.euid },
//         token: cookies.e_t,
//       });
//       if (response) {
//         setNotificationData(response?.data || []);
//       }
//     } catch (error) {
//       console.error("Error fetching read notifications:", error);
//     } finally {
//       setLoading(false);
//     }
//   }, [cookies.euid, cookies.e_t]);

//   // Handle mark single notification as read
//   const markRead = async (notificationId) => {
//     try {
//       const response = await patchData({
//         endpoint: `Notification/${notificationId}/read`,
//         params: { notificationId: notificationId },
//         token: cookies.e_t,
//       });
//       if (response && response.success) {
//         // Update local state immediately
//         setNotificationData(prev => 
//           prev.map(notif => 
//             notif.id === notificationId ? { ...notif, isRead: true } : notif
//           )
//         );
//         // If we're in unread filter, remove the notification from view
//         if (filter === 'unread') {
//           setNotificationData(prev => 
//             prev.filter(notif => notif.id !== notificationId)
//           );
//         }
//       }
//     } catch (error) {
//       console.error("Error marking notification as read:", error);
//       // Optional: Re-fetch data if update fails
//       getAllNotifications();
//     }
//   };

//   // Handle mark all notifications as read
//   const markAllAsRead = async () => {
//     try {
//       const response = await patchData({
//         endpoint: `Notification/employee/${cookies.euid}/read-all`,
//         params: { employeeId: cookies.euid },
//         token: cookies.e_t,
//       });
//       if (response && response.success) {
//         // Update local state immediately
//         setNotificationData(prev => 
//           prev.map(notif => ({ ...notif, isRead: true }))
//         );
//         // If we're in unread filter, clear all notifications
//         if (filter === 'unread') {
//           setNotificationData([]);
//         }
//       }
//     } catch (error) {
//       console.error("Error marking all notifications as read:", error);
//       // Optional: Re-fetch data if update fails
//       getAllNotifications();
//     }
//   };

//   // Handle deleting a notification
//   const deleteNotification = async (id) => {
//     try {
//       const response = await postData({
//         endpoint: 'Notification/delete',
//         data: { notificationId: id },
//         token: cookies.e_t,
//       });
//       if (response && response.success) {
//         setNotificationData(prev => prev.filter(notif => notif.id !== id));
//       }
//     } catch (error) {
//       console.error("Error deleting notification:", error);
//     }
//   };

//   // Get icon based on notification type
//   const getIcon = (type) => {
//     switch(type) {
//       case 'Message':
//         return <MessageSquare className="icon message" size={18} />;
//       case 'Community':
//         return <Mail className="icon mention" size={18} />;
//       case 'Post':
//         return <Star className="icon like" size={18} />;
//       case 'Report':
//         return <AlertCircle className="icon alert" size={18} />;
//       case 'TaskAssigned':
//         return <FileText className="icon task" size={18} />;
//       default:
//         return <Bell className="icon default" size={18} />;
//     }
//   };

//   // Load appropriate data when filter changes
//   useEffect(() => {
//     if (filter === 'all') {
//       getAllNotifications();
//     } else if (filter === 'unread') {
//       getAllUnreadNotifications();
//     } else if (filter === 'read') {
//       getAllReadNotifications();
//     }
//   }, [filter, getAllNotifications, getAllUnreadNotifications, getAllReadNotifications]);

//   return (
//     <div className="notifications-container">
//        <div className="notifications-filters">
//          <div className="filter-buttons">
//           <button 
//             onClick={() => setFilter('all')}
//             className={filter === 'all' ? 'active' : ''}
//           >
//             All
//           </button>
//           <button 
//             onClick={() => setFilter('unread')}
//             className={filter === 'unread' ? 'active' : ''}
//           >
//             Unread
//           </button>
//           <button 
//             onClick={() => setFilter('read')}
//             className={filter === 'read' ? 'active' : ''}
//           >
//             Read
//           </button>
//         </div>
//         <div className="header-actions">
//           <button onClick={markAllAsRead} className="mark-all-read">
//             <Check size={16} />
//             Mark all as read
//           </button>
//         </div>
//       </div>

//       <div className="notifications-list">
//         {loading ? (
//           <div className="loading-state">
//             <p>Loading notifications...</p>
//           </div>
//         ) : notificationData && notificationData.length > 0 ? (
//           notificationData.map((notification) => (
//             <div 
//               key={notification.id} 
//               className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
//             >
//               <div className="notification-icon">
//                 {getIcon(notification.type)}
//               </div>
//               <div className="notification-content">
//                 <p className="notification-text">{notification.message}</p>
//                 <p className="notification-time">{new Date(notification.createdDate).toLocaleString()}</p>
//               </div>
//               <div className="notification-actions">
//                 {!notification.isRead && (
//                   <button 
//                     onClick={() => markRead(notification.id)}
//                     className="action-button read"
//                     title="Mark as read"
//                   >
//                     <Check size={16} />
//                   </button>
//                 )}
//                 {/* <button 
//                   onClick={() => deleteNotification(notification.id)}
//                   className="action-button delete"
//                   title="Delete notification"
//                 >
//                   <Trash2 size={16} />
//                 </button> */}
//               </div>
//             </div>
//           ))
//         ) : (
//           <div className="empty-state">
//             <div className="empty-icon">
//               <Bell size={24} />
//             </div>
//             <p className="empty-title">No notifications found</p>
//             <p className="empty-subtitle">
//               {filter !== 'all' ? 'Try changing your filter selection' : 'You\'re all caught up!'}
//             </p>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default Notifications;